<aside class="summary-panel" aria-label="Bill summary">
  <!-- Bill / counter -->
  <header class="summary-head">
    <div class="summary-head__item">
      <span class="summary-eyebrow">Bill no</span>
      <!-- Bill No — managed by this component (WebSocket). Changed digits flip like a split-flap board. -->
      <span class="bill-no num" [attr.aria-label]="'Bill number ' + getBillNumberDigits().join('')">
        <span
          class="flap"
          *ngFor="let digit of getBillNumberDigits(); let i = index; trackBy: trackByIndex"
          [class.flipping]="isFlipping[i]"
        >
          <span class="flap__half flap__half--top"><span>{{ digit }}</span></span>
          <span class="flap__half flap__half--bottom"
            ><span>{{ isFlipping[i] ? previousBillDigits[i] : digit }}</span></span
          >
          <ng-container *ngIf="isFlipping[i]">
            <span class="flap__leaf flap__leaf--top"><span>{{ previousBillDigits[i] }}</span></span>
            <span class="flap__leaf flap__leaf--bottom"><span>{{ digit }}</span></span>
          </ng-container>
        </span>
      </span>
    </div>

    <!-- Counter — managed by this component (reads AuthService); the name already reads "Counter 1" -->
    <div class="summary-head__item summary-head__item--end" [title]="'Billing counter: ' + getCounterNumber()">
      <span class="counter-chip">
        <i class="pi pi-desktop" aria-hidden="true"></i>
        <span class="counter-chip__name">{{ getCounterNumber() }}</span>
      </span>
    </div>
  </header>

  <!-- Mobile shop margin reference -->
  <div class="ref-strip" *ngIf="isMobileIndustry && totalQuantity > 0">
    <span class="summary-eyebrow">Ref</span>
    <span class="ref-code num">
      {{ getBusinessNamePrefix() }} {{ profitAmount | number: "1.2-2" }} ANK
    </span>
  </div>

  <div class="summary-body">
    <!-- Counts -->
    <div class="summary-stats">
      <!-- *ngFor over [value] re-creates the span only when the value changes, which replays the pop animation -->
      <div class="stat stat--items">
        <i class="pi pi-shopping-cart stat__icon" aria-hidden="true"></i>
        <span class="stat__body">
          <span class="stat__value num" *ngFor="let v of [uniqueItemsCount]">{{ v }}</span>
          <span class="stat__label">Items</span>
        </span>
      </div>
      <div class="stat stat--qty">
        <i class="pi pi-box stat__icon" aria-hidden="true"></i>
        <span class="stat__body">
          <span class="stat__value num" *ngFor="let v of [totalQuantity]">{{ v }}</span>
          <span class="stat__label">Qty</span>
        </span>
      </div>
      <div class="stat stat--free" *ngIf="freeQuantity > 0 && isFreeQtyEnabled">
        <i class="pi pi-gift stat__icon" aria-hidden="true"></i>
        <span class="stat__body">
          <span class="stat__value num" *ngFor="let v of [freeQuantity]">{{ v }}</span>
          <span class="stat__label">Free</span>
        </span>
      </div>
    </div>

    <!-- Breakdown -->
    <dl class="summary-lines">
      <div class="line">
        <dt>Subtotal</dt>
        <dd class="num">{{ subtotal | currency: "INR" : "symbol" : "1.2-2" }}</dd>
      </div>

      <div class="line" *ngIf="isInterStateTaxOrder">
        <dt>IGST</dt>
        <dd class="num">{{ igst | currency: "INR" : "symbol" : "1.2-2" }}</dd>
      </div>
      <ng-container *ngIf="!isInterStateTaxOrder">
        <div class="line">
          <dt>CGST</dt>
          <dd class="num">{{ cgst | currency: "INR" : "symbol" : "1.2-2" }}</dd>
        </div>
        <div class="line">
          <dt>SGST</dt>
          <dd class="num">{{ sgst | currency: "INR" : "symbol" : "1.2-2" }}</dd>
        </div>
      </ng-container>

      <div class="line" [class.line--muted]="!productDiscount">
        <dt>Item discount</dt>
        <dd class="num">{{ productDiscount | currency: "INR" : "symbol" : "1.2-2" }}</dd>
      </div>

      <div class="line" *ngIf="freeQuantity > 0 && isFreeQtyEnabled">
        <dt>Free items value</dt>
        <dd class="num">{{ totalFreeAmount | currency: "INR" : "symbol" : "1.2-2" }}</dd>
      </div>

      <div class="line line--positive" *ngIf="enableOverAllDiscount && !isExchangeOrder && overAllDiscountAmount > 0">
        <dt>Bill discount</dt>
        <dd class="num">−{{ overAllDiscountAmount | currency: "INR" : "symbol" : "1.2-2" }}</dd>
      </div>

      <!-- Free Amount deduction – Mobile industry only -->
      <div class="line line--positive" *ngIf="isMobileIndustry && freeAmount > 0">
        <dt>Free amount</dt>
        <dd class="num">−{{ freeAmount | currency: "INR" : "symbol" : "1.2-2" }}</dd>
      </div>

      <div class="line" *ngIf="roundOffAmount !== 0">
        <dt>Round off</dt>
        <dd class="num">{{ roundOffAmount | currency: "INR" : "symbol" : "1.2-2" }}</dd>
      </div>
    </dl>

    <!-- Overall (bill) discount entry -->
    <div class="bill-discount" *ngIf="enableOverAllDiscount && !isExchangeOrder">
      <label class="bill-discount__label" for="overallDiscountInput">Bill discount</label>
      <div class="bill-discount__control">
        <p-selectButton
          [options]="discountModeOptions"
          [(ngModel)]="overAllDiscountMode"
          (onChange)="discountModeChanged.emit($event)"
          styleClass="bill-discount__mode"
        >
        </p-selectButton>
        <input
          id="overallDiscountInput"
          type="number"
          inputmode="decimal"
          class="bill-discount__input num"
          [value]="discountRawValue"
          (input)="onDiscountInput($event)"
          (blur)="onDiscountBlur()"
          [placeholder]="overAllDiscountMode === 'percentage' ? '0 %' : '₹ 0'"
          [max]="overAllDiscountMode === 'percentage' ? 100 : subtotalAfterLineDiscount"
        />
      </div>
    </div>
  </div>

  <!-- Total + pay -->
  <footer class="summary-foot">
    <div class="net-total" [class.net-total--zero]="roundedTotal <= 0">
      <span class="net-total__label">Net amount</span>
      <span class="net-total__value num" *ngFor="let v of [roundedTotal]">{{ v | currency: "INR" : "symbol" : "1.2-2" }}</span>
    </div>

    <p class="summary-hint summary-hint--danger" *ngIf="isOrderBlocked && roundedTotal > 0" role="alert">
      <i class="pi pi-wifi" aria-hidden="true"></i>
      {{ isNetworkOffline ? "Network offline — order blocked" : "Network slow — order blocked" }}
    </p>
    <p class="summary-hint" *ngIf="roundedTotal <= 0 && !allowFreeOnlyCheckout">Add items to take payment</p>

    <button
      type="button"
      class="pay-btn"
      [class.pay-btn--quotation]="isQuotationMode"
      [class.pay-btn--return]="isExchangeOrder"
      [disabled]="
        isQuotationMode
          ? !canCompleteQuotation || isProcessingQuotation
          : (roundedTotal <= 0 && !allowFreeOnlyCheckout) || hasPendingVariantSelection || isOrderBlocked
      "
      (click)="processOrderClicked.emit()"
      [attr.aria-label]="isQuotationMode ? 'Process quotation and generate PDF' : 'Process payment and complete order'"
    >
      <i
        class="pi"
        [ngClass]="isQuotationMode && isProcessingQuotation ? 'pi-spin pi-spinner' : isExchangeOrder ? 'pi-replay' : isQuotationMode ? 'pi-file-pdf' : 'pi-credit-card'"
        aria-hidden="true"
      ></i>
      <!-- getProcessButtonText() is owned by this component -->
      <span class="pay-btn__label">{{ getProcessButtonText() }}</span>
      <span class="pay-btn__key">F7</span>
    </button>
  </footer>
</aside>
